/**
 * Zod validation schemas for Product types
 */

import { z } from 'zod';

export const productSchema = z.object({
  id: z.string(),
  name: z.string().min(1, 'Product name is required'),
  description: z.string().optional(),
  category_id: z.string().optional().nullable(),
  subcategory_id: z.string().optional().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});

/**
 * @deprecated Use productVariantSchema from @/schemas/variants instead
 * This schema is kept for backward compatibility but should be updated to match the new API structure
 */
export const productVariantSchema = z.object({
  id: z.string(),
  product_id: z.string(),
  variant_name: z.string().min(1, 'Variant name is required'),
  sku: z.string().optional(),
  barcode: z.string().optional(),
  pack_size: z.string().min(1, 'Pack size is required'),
  quantity: z.string().min(1, 'Quantity is required'),
  description: z.string().optional(),
  collaborator_ids: z.array(z.string()), // Updated to match new API (array instead of single id)
  brand_name: z.string().optional(),
  hsn_code: z.string().optional(),
  gst_rate: z.number().min(0).max(100).optional(),
  prices: z.array(z.any()).optional(), // Prices array
  dosage_instructions: z.string().optional(),
  usage_details: z.string().optional(),
  images: z.array(z.string()).optional(),
  is_active: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
});

export const productPriceSchema = z.object({
  id: z.string(),
  variant_id: z.string(),
  price_type: z.enum(['PURCHASE', 'SALE', 'MRP']),
  price: z.number().positive('Price must be positive'),
  currency: z.string().default('INR'),
  effective_from: z.string().optional(),
  effective_to: z.string().optional(),
  is_active: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
});

export const createProductSchema = productSchema.omit({
  id: true,
  created_at: true,
  updated_at: true,
}).extend({
  category_id: z.string().optional(),
  subcategory_id: z.string().optional(),
});

export const createProductVariantSchema = productVariantSchema.omit({
  id: true,
  created_at: true,
  updated_at: true,
  is_active: true,
});

export const createProductPriceSchema = productPriceSchema.omit({
  id: true,
  created_at: true,
  updated_at: true,
  is_active: true,
});

export type Product = z.infer<typeof productSchema>;
export type ProductVariant = z.infer<typeof productVariantSchema>;
export type ProductPrice = z.infer<typeof productPriceSchema>;
export type CreateProduct = z.infer<typeof createProductSchema>;
export type CreateProductVariant = z.infer<typeof createProductVariantSchema>;
export type CreateProductPrice = z.infer<typeof createProductPriceSchema>;
